import { useState } from 'react'
import {
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import moment from 'moment'
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'
import { Hold } from 'app/models/hold-params'

interface Props {
  opened: boolean
  close: () => void
  entityNoun?: string
  hold?: Hold | null
  onSubmit: (liftReason?: string) => Promise<boolean>
}

const formatPlacedOn = (raw?: string | null) => {
  if (!raw) return '—'
  const m = moment(raw)
  return m.isValid() ? m.format('MMMM Do, YYYY [at] h:mm A') : '—'
}

const formatExpires = (raw?: string | null) => {
  if (!raw) return null
  const m = moment(raw)
  return m.isValid() ? m.format('MMMM Do, YYYY') : null
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Text className="lock-detail-text">
      <Text component="span" className="lock-detail-label">
        {label}:
      </Text>{' '}
      {value}
    </Text>
  )
}

// Generic lift-hold confirmation. Shows the current hold details and an
// optional lift reason. Network/entity specifics come via onSubmit.
export default function LiftHoldModal({
  opened,
  close,
  hold,
  onSubmit,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const form = useForm<{ liftReason: string }>({
    initialValues: { liftReason: '' },
  })

  const handleSubmit = async (values: { liftReason: string }) => {
    setIsLoading(true)
    const liftReason = values.liftReason.trim()
    let ok = false
    try {
      ok = await onSubmit(liftReason || undefined)
    } finally {
      setIsLoading(false)
    }
    if (ok) {
      form.reset()
      close()
    }
  }

  const handleClose = () => {
    form.reset()
    close()
  }

  const expires = hold ? formatExpires(hold.expirationDate) : null

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      withCloseButton={false}
      size="25%"
    >
      <Stack w="100%" p={12} className="lock-modal-stack">
        <div className="cancel-open-trade-header">
          <Text
            className="cancel-open-trade-title"
            style={{ fontSize: 20, lineHeight: '26px' }}
          >
            Lift Lock
          </Text>
          <Button
            onClick={handleClose}
            variant="subtle"
            style={{
              padding: 0,
              background: 'transparent',
              height: 30,
              width: 30,
              minWidth: 'auto',
            }}
            aria-label="Close modal"
          >
            <CloseIcon style={{ width: '30px' }} />
          </Button>
        </div>
        <Text color="dimmed" className="lock-modal-description">
          Are you sure you want to lift this lock? This will allow approvals and
          signing to proceed.
        </Text>

        {hold && (
          <Box p={16} className="lock-info-box">
            <Stack className="lock-info-stack">
              <DetailRow
                label="Original Lock Reason"
                value={hold.reason || '—'}
              />
              <DetailRow label="Placed By" value={hold.placedByName || '—'} />
              <DetailRow label="Placed On" value={formatPlacedOn(hold.placedAt)} />
              {expires && <DetailRow label="Expires" value={expires} />}
              <DetailRow label="Notes" value={hold.notes || '—'} />
            </Stack>
          </Box>
        )}

        <form style={{ width: '100%' }} onSubmit={form.onSubmit(handleSubmit)}>
          <Stack className="lock-form-stack">
            <Textarea
              label="Reason for Lifting Lock (Optional)"
              placeholder="Enter the reason for lifting this lock"
              minRows={3}
              size="md"
              autosize
              classNames={{ label: 'lock-field-label', input: 'lock-field-input' }}
              {...form.getInputProps('liftReason')}
            />
            <Group position="right" mt="md">
              <Button
                className="cancel-open-trade-cancel-button"
                onClick={handleClose}
                disabled={isLoading}
              >
                <div className="cancel-open-trade-cancel-button-text">
                  Cancel
                </div>
              </Button>
              <Button
                type="submit"
                className="cancel-open-trade-yes-button"
                disabled={isLoading}
              >
                <div className="cancel-open-trade-yes-button-text">
                  {isLoading ? 'Processing...' : 'Lift Lock'}
                </div>
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Modal>
  )
}
